import { z } from 'zod';
import { ConversationIdSchema, MessageIdSchema, RunIdSchema, UserIdSchema } from '../ids.ts';

const booleanQuery = z.enum(['true', 'false']).transform((value) => value === 'true');
const dateQuery = z.iso.datetime({ offset: true }).or(z.iso.date());

export const ConsoleConversationListQuerySchema = z.object({
  rating: z.enum(['up', 'down', 'none']).optional(),
  has_error: booleanQuery.optional(),
  organization: z.string().min(1).max(256).optional(),
  from: dateQuery.optional(),
  to: dateQuery.optional(),
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ConsoleConversationListQuery = z.input<typeof ConsoleConversationListQuerySchema>;

export const ConsoleConversationSummarySchema = z.object({
  conversation_id: ConversationIdSchema,
  user_id: UserIdSchema,
  user_subject: z.string(),
  organization: z.string().nullable(),
  title: z.string().nullable(),
  purpose: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  runs: z.number(),
  failed_runs: z.number(),
  tool_calls: z.number(),
  failed_tool_calls: z.number(),
  thumbs_up: z.number(),
  thumbs_down: z.number(),
});
export type ConsoleConversationSummary = z.infer<typeof ConsoleConversationSummarySchema>;

export const ConsoleConversationListSchema = z.object({
  data: z.array(ConsoleConversationSummarySchema),
  total: z.number(),
  next_cursor: z.string().nullable(),
});
export type ConsoleConversationList = z.infer<typeof ConsoleConversationListSchema>;

export const ConsoleTraceMessageSchema = z.object({
  id: MessageIdSchema,
  role: z.enum(['user', 'assistant']),
  run_id: RunIdSchema.nullable(),
  position: z.number(),
  created_at: z.string(),
  model_id: z.string().nullable(),
  parts: z.array(z.unknown()),
});
export type ConsoleTraceMessage = z.infer<typeof ConsoleTraceMessageSchema>;

export const ConsoleTraceToolCallSchema = z.object({
  tool_call_id: z.string(),
  tool_name: z.string(),
  tool_kind: z.string(),
  mcp_server: z.string().nullable(),
  mcp_tool_name: z.string().nullable(),
  arguments: z.unknown(),
  started_at: z.string(),
  status: z.string().nullable(),
  error_type: z.string().nullable(),
  duration_ms: z.number().nullable(),
  result_bytes: z.number().nullable(),
});
export type ConsoleTraceToolCall = z.infer<typeof ConsoleTraceToolCallSchema>;

export const ConsoleTraceRunSchema = z.object({
  id: RunIdSchema,
  status: z.string(),
  stop_reason: z.string().nullable(),
  model_id: z.string().nullable(),
  model_route: z.string().nullable(),
  purpose: z.string(),
  started_at: z.string(),
  ended_at: z.string().nullable(),
  usage: z.record(z.string(), z.unknown()).nullable(),
  system_prompt: z.unknown(),
  tools: z.unknown(),
  messages: z.array(z.unknown()),
  reply_message_id: MessageIdSchema.nullable(),
  tool_calls: z.array(ConsoleTraceToolCallSchema),
});
export type ConsoleTraceRun = z.infer<typeof ConsoleTraceRunSchema>;

export const ConsoleTraceFeedbackSchema = z.object({
  message_id: MessageIdSchema,
  rating: z.enum(['up', 'down']),
  comment: z.string().nullable(),
  updated_at: z.string(),
});

export const ConsoleConversationTraceSchema = z.object({
  conversation: z.object({
    id: ConversationIdSchema,
    user_id: UserIdSchema,
    title: z.string().nullable(),
    purpose: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
  }),
  messages: z.array(ConsoleTraceMessageSchema),
  runs: z.array(ConsoleTraceRunSchema),
  feedback: z.array(ConsoleTraceFeedbackSchema),
});
export type ConsoleConversationTrace = z.infer<typeof ConsoleConversationTraceSchema>;

export const ConsoleEvalCaseSavedSchema = z.object({ eval_case_id: z.string() });
export type ConsoleEvalCaseSaved = z.infer<typeof ConsoleEvalCaseSavedSchema>;

export const ConsoleToolHealthQuerySchema = z.object({
  from: dateQuery.optional(),
  to: dateQuery.optional(),
});

export const ConsoleToolHealthRowSchema = z.object({
  day: z.string(),
  tool_name: z.string(),
  tool_kind: z.string(),
  mcp_server: z.string().nullable(),
  calls: z.number(),
  failures: z.number(),
  p50_ms: z.number(),
  p95_ms: z.number(),
});
export type ConsoleToolHealthRow = z.infer<typeof ConsoleToolHealthRowSchema>;

export const ConsoleToolHealthSchema = z.object({
  from: z.string(),
  to: z.string(),
  data: z.array(ConsoleToolHealthRowSchema),
});
export type ConsoleToolHealth = z.infer<typeof ConsoleToolHealthSchema>;
